import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { moderateMessage } from "@/lib/safety/moderateMessage";
import { logBehavioralSignal } from "@/lib/safety/useBehavioralSignals";
import {
  detectMessageSignals,
  detectRapidMessaging,
} from "@/lib/safety/behavioralHeuristics";
import type { MessageKind, MessageRow } from "./types";

/**
 * Messages for a single conversation, with realtime appends and
 * automatic mark-read on mount + on new inbound messages.
 */
export const useMessages = (conversationId: string | null) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const seenIds = useRef<Set<string>>(new Set());
  // Rolling window of recent outbound send timestamps for rapid-messaging detection.
  const recentSends = useRef<number[]>([]);

  const markRead = useCallback(async () => {
    if (!conversationId) return;
    await supabase.rpc("fn_mark_read", { _conversation_id: conversationId });
  }, [conversationId]);

  const load = useCallback(async () => {
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true })
      .limit(500);
    if (error) {
      setMessages([]);
      setLoading(false);
      return;
    }
    const rows = (data as MessageRow[]) ?? [];
    seenIds.current = new Set(rows.map((r) => r.id));
    setMessages(rows);
    setLoading(false);
    void markRead();
  }, [conversationId, markRead]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`conv:${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const row = payload.new as MessageRow;
          if (seenIds.current.has(row.id)) return;
          seenIds.current.add(row.id);
          setMessages((prev) => [...prev, row]);
          if (row.sender_id !== user?.id) void markRead();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [conversationId, user, markRead]);

  const send = useCallback(
    async (
      kind: MessageKind,
      opts: { body?: string; mediaUrl?: string; durationMs?: number } = {},
    ) => {
      if (!conversationId) return { error: "no_conversation" };
      setSending(true);
      const { data, error } = await supabase.rpc("fn_send_message", {
        _conversation_id: conversationId,
        _kind: kind,
        _body: opts.body ?? null,
        _media_url: opts.mediaUrl ?? null,
        _duration_ms: opts.durationMs ?? null,
      });
      setSending(false);
      // RAELA Function 1 — classify after successful insert. Fire-and-forget.
      if (!error && kind === "text" && opts.body && typeof data === "string") {
        moderateMessage("dm", data, opts.body);

        // Behavioral risk signals — content + velocity heuristics.
        // All fire-and-forget; the edge function returns nudges via
        // moderation_log which the UI subscribes to via useSafetyAlerts.
        const messageId = data;
        const now = Date.now();
        recentSends.current = [...recentSends.current.filter((t) => now - t < 60_000), now];

        const contentSignals = detectMessageSignals(opts.body);
        for (const s of contentSignals) {
          logBehavioralSignal({
            kind: s.kind,
            evidence: s.evidence,
            conversationId,
            messageId,
          });
        }
        const rapid = detectRapidMessaging(recentSends.current, now);
        if (rapid) {
          logBehavioralSignal({
            kind: rapid.kind,
            evidence: rapid.evidence,
            conversationId,
            messageId,
          });
        }
      }
      return { error: error?.message ?? null };
    },
    [conversationId],
  );

  return { messages, loading, sending, send, reload: load };
};
